import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import changeHandler from "common/form/changeHandler";
import submitHandler from "common/form/submitHandler";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Card,
  Banner,
  Page,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import {
  setFormData,
  submit,
} from "features/company-create/company-create.slice";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "configureStore";

// Define types for form controls
type FormControl = {
  value: string;
  [key: string]: any;
};

type FormControls = {
  [key: string]: FormControl;
};

type CompanyFormPayload = {
  name: string;
  legalName: string;
  code: string;
  vat: string;
  address: string;
  description: string;
  bankName: string;
  bankIban: string;
};

const CompanyCreate: React.FC = () => {
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errMsg, setErrMsg] = useState<string>("");
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const { formControls, isLoading, isFormValid, isSuccessfullySaved } =
    useSelector((state: RootState) => state.companyCreateReducer);

  const handleFormChange = (formData: FormControls) => {
    dispatch(setFormData(formData));
  };

  const handleFormSubmit = async (formData: CompanyFormPayload) => {
    try {
      const result = await dispatch(submit(formData));
      const payload = result as { selectedCompany?: string };

      if (payload?.selectedCompany) {
        navigate(`/app/${payload.selectedCompany}/about-company`);
      }
      setSuccessMessage("ok");
    } catch (e) {
      setErrMsg("error");
    }
  };

  return (
    <div className="mx-auto w-full md:w-1/2">
      <Page fullWidth title={t("create_company_form")}>
        <div className="company-create">
          <Card>
            <Form onSubmit={submitHandler(formControls, handleFormSubmit)}>
              <FormLayout>
                <div className="form-warnings">
                  {errMsg && (
                    <div>
                      <Banner tone="critical">{t(errMsg)}</Banner>
                    </div>
                  )}
                  {successMessage && (
                    <div>
                      <Banner tone="info">{t(successMessage)}</Banner>
                    </div>
                  )}
                </div>
                <TextField
                  label={t("company_name")}
                  type="text"
                  id="name"
                  autoComplete="off"
                  value={formControls.name.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <TextField
                  label={t("legal_name")}
                  type="text"
                  id="legalName"
                  autoComplete="off"
                  value={formControls.legalName.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <TextField
                  label={t("company_code")}
                  type="text"
                  id="code"
                  autoComplete="off"
                  value={formControls.code.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <TextField
                  label={t("company_vat")}
                  type="text"
                  id="vat"
                  autoComplete="off"
                  value={formControls.vat.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <TextField
                  label={t("bank_name")}
                  type="text"
                  id="bankName"
                  autoComplete="off"
                  value={formControls.bankName.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <TextField
                  label={t("bank_iban")}
                  type="text"
                  id="bankIban"
                  autoComplete="off"
                  value={formControls.bankIban.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <TextField
                  label={t("company_address")}
                  type="text"
                  id="address"
                  autoComplete="off"
                  value={formControls.address.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <TextField
                  label={t("company_description")}
                  type="text"
                  id="description"
                  autoComplete="off"
                  multiline={4}
                  value={formControls.description.value}
                  onChange={changeHandler(formControls, handleFormChange)}
                />
                <Button loading={isLoading} disabled={!isFormValid} submit>
                  {t("create_company")}
                </Button>
              </FormLayout>
            </Form>
          </Card>
        </div>
      </Page>
    </div>
  );
};

export default CompanyCreate;
